import { EventEmitter } from 'events';
import { watch, FSWatcher } from 'chokidar';
import path from 'path';
import fs from 'fs-extra';
import { minimatch } from 'minimatch';
import { logger } from '../../utils/logger';
import { WorkflowEngine, WorkflowDefinition, WorkflowInstance } from './workflow-engine';
import { HookManager } from '../hooks/hook-manager';
import { TaskStore } from '../task-store';
import { PRDGenerator } from '../prd-generator';
import { DesignDocManager } from '../design-doc-manager';
import { EARSParser } from '../ears/ears-parser';

/**
 * 触发器类型
 */
export enum TriggerType {
  FILE_CHANGE = 'file_change',
  TASK_STATUS_CHANGE = 'task_status_change',
  DOCUMENT_APPROVAL = 'document_approval',
  SCHEDULE = 'schedule',
  WEBHOOK = 'webhook',
  MANUAL = 'manual',
  EARS_REQUIREMENT_ADDED = 'ears_requirement_added'
}

/**
 * 触发器条件
 */
export interface TriggerCondition {
  type: 'pattern' | 'expression' | 'function';
  pattern?: string; // 文件模式匹配
  expression?: string; // 表达式
  handler?: (context: TriggerContext) => boolean | Promise<boolean>;
}

/**
 * 触发器上下文
 */
export interface TriggerContext {
  type: TriggerType;
  filePath?: string;
  taskId?: string;
  documentId?: string;
  oldContent?: string;
  newContent?: string;
  changes?: any;
  metadata?: Record<string, any>;
}

/**
 * 工作流触发器配置
 */
export interface WorkflowTrigger {
  id: string;
  name: string;
  type: TriggerType;
  enabled: boolean;
  conditions: TriggerCondition[];
  workflowId: string;
  variables?: Record<string, any>;
  priority?: number;
  debounce?: number; // 防抖延迟（毫秒）
  cooldown?: number; // 冷却时间（毫秒）
}

/**
 * 智能工作流触发器管理器
 */
export class WorkflowTriggerManager extends EventEmitter {
  private triggers = new Map<string, WorkflowTrigger>();
  private watchers = new Map<string, FSWatcher>();
  private lastTriggerTime = new Map<string, number>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();
  private workflowEngine: WorkflowEngine;
  private hookManager: HookManager;
  private taskStore: TaskStore;
  
  constructor(
    workflowEngine: WorkflowEngine,
    hookManager: HookManager,
    taskStore: TaskStore
  ) {
    super();
    this.workflowEngine = workflowEngine;
    this.hookManager = hookManager;
    this.taskStore = taskStore;
    
    // 监听内部事件
    this.setupInternalListeners();
  }
  
  /**
   * 设置内部监听器
   */
  private setupInternalListeners(): void {
    // 监听任务状态变化
    this.taskStore.on('taskStatusChanged', async (data) => {
      await this.handleTaskStatusChange(data);
    });
    
    // 监听文档审批事件
    this.hookManager.on('documentApproved', async (data) => {
      await this.handleDocumentApproval(data);
    });
    
    // 监听 EARS 需求变化
    this.hookManager.on('earsRequirementAdded', async (data) => {
      await this.handleEarsRequirementAdded(data);
    });
  }
  
  /**
   * 注册触发器
   */
  async registerTrigger(trigger: WorkflowTrigger): Promise<void> {
    this.validateTrigger(trigger);
    
    // 保存触发器
    this.triggers.set(trigger.id, trigger);
    
    // 根据类型设置监听
    if (trigger.enabled) {
      await this.setupTriggerListener(trigger);
    }
    
    logger.info(`Registered workflow trigger: ${trigger.name} (${trigger.id})`);
    this.emit('triggerRegistered', trigger);
  }
  
  /**
   * 设置触发器监听
   */
  private async setupTriggerListener(trigger: WorkflowTrigger): Promise<void> {
    switch (trigger.type) {
      case TriggerType.FILE_CHANGE:
        await this.setupFileWatcher(trigger);
        break;
      case TriggerType.SCHEDULE:
        await this.setupScheduler(trigger);
        break;
      // 其他类型的触发器通过事件监听实现
    }
  }
  
  /**
   * 设置文件监听器
   */
  private async setupFileWatcher(trigger: WorkflowTrigger): Promise<void> {
    const patterns = trigger.conditions
      .filter(c => c.type === 'pattern' && c.pattern)
      .map(c => c.pattern!);
    
    if (patterns.length === 0) {
      logger.warn(`No file patterns found for trigger ${trigger.id}`);
      return;
    }
    
    const watcher = watch(patterns, {
      persistent: true,
      ignoreInitial: true,
      awaitWriteFinish: {
        stabilityThreshold: 1000,
        pollInterval: 100
      }
    });
    
    watcher.on('change', async (filePath) => {
      await this.handleFileChange(trigger, filePath);
    });
    
    watcher.on('add', async (filePath) => {
      await this.handleFileChange(trigger, filePath);
    });
    
    watcher.on('error', (error) => {
      logger.error(`Watch error for trigger ${trigger.id}:`, error);
    });
    
    this.watchers.set(trigger.id, watcher);
  }
  
  /**
   * 处理文件变更
   */
  private async handleFileChange(trigger: WorkflowTrigger, filePath: string): Promise<void> {
    const context: TriggerContext = {
      type: TriggerType.FILE_CHANGE,
      filePath,
      metadata: {
        trigger: trigger.id
      }
    };
    
    // 检查条件
    if (!await this.checkConditions(trigger, context)) {
      return;
    }
    
    // 触发工作流
    await this.triggerWorkflow(trigger, context);
  }
  
  /**
   * 处理任务状态变化
   */
  private async handleTaskStatusChange(data: any): Promise<void> {
    const triggers = Array.from(this.triggers.values())
      .filter(t => t.enabled && t.type === TriggerType.TASK_STATUS_CHANGE);
    
    for (const trigger of triggers) {
      const context: TriggerContext = {
        type: TriggerType.TASK_STATUS_CHANGE,
        taskId: data.taskId,
        changes: data,
        metadata: {
          oldStatus: data.oldStatus,
          newStatus: data.newStatus
        }
      };
      
      if (await this.checkConditions(trigger, context)) {
        await this.triggerWorkflow(trigger, context);
      }
    }
  }
  
  /**
   * 处理文档审批
   */
  private async handleDocumentApproval(data: any): Promise<void> {
    const triggers = Array.from(this.triggers.values())
      .filter(t => t.enabled && t.type === TriggerType.DOCUMENT_APPROVAL);
    
    for (const trigger of triggers) {
      const context: TriggerContext = {
        type: TriggerType.DOCUMENT_APPROVAL,
        documentId: data.documentId,
        metadata: data
      };
      
      if (await this.checkConditions(trigger, context)) {
        await this.triggerWorkflow(trigger, context);
      }
    }
  }
  
  /**
   * 处理 EARS 需求添加
   */
  private async handleEarsRequirementAdded(data: any): Promise<void> {
    const triggers = Array.from(this.triggers.values())
      .filter(t => t.enabled && t.type === TriggerType.EARS_REQUIREMENT_ADDED);
    
    for (const trigger of triggers) {
      const context: TriggerContext = {
        type: TriggerType.EARS_REQUIREMENT_ADDED,
        metadata: {
          requirement: data.requirement,
          source: data.source
        }
      };
      
      if (await this.checkConditions(trigger, context)) {
        await this.triggerWorkflow(trigger, context);
      }
    }
  }
  
  /**
   * 检查触发条件
   */
  private async checkConditions(trigger: WorkflowTrigger, context: TriggerContext): Promise<boolean> {
    // 检查冷却时间
    if (trigger.cooldown) {
      const lastTime = this.lastTriggerTime.get(trigger.id) || 0;
      if (Date.now() - lastTime < trigger.cooldown) {
        logger.debug(`Trigger ${trigger.id} is in cooldown period`);
        return false;
      }
    }
    
    // 检查所有条件
    for (const condition of trigger.conditions) {
      let result = false;
      
      switch (condition.type) {
        case 'pattern':
          if (context.filePath && condition.pattern) {
            result = minimatch(context.filePath, condition.pattern);
          }
          break;
          
        case 'expression':
          // TODO: 实现表达式求值
          result = true;
          break;
          
        case 'function':
          if (condition.handler) {
            result = await condition.handler(context);
          }
          break;
      }
      
      if (!result) {
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * 触发工作流
   */
  private async triggerWorkflow(trigger: WorkflowTrigger, context: TriggerContext): Promise<void> {
    // 防抖处理
    if (trigger.debounce) {
      const existingTimer = this.debounceTimers.get(trigger.id);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }
      
      const timer = setTimeout(async () => {
        await this.doTriggerWorkflow(trigger, context);
        this.debounceTimers.delete(trigger.id);
      }, trigger.debounce);
      
      this.debounceTimers.set(trigger.id, timer);
    } else {
      await this.doTriggerWorkflow(trigger, context);
    }
  }
  
  /**
   * 实际触发工作流
   */
  private async doTriggerWorkflow(trigger: WorkflowTrigger, context: TriggerContext): Promise<void> {
    try {
      // 记录触发时间
      this.lastTriggerTime.set(trigger.id, Date.now());
      
      // 准备变量
      const variables = {
        ...trigger.variables,
        triggerContext: context,
        triggeredAt: new Date().toISOString(),
        triggeredBy: trigger.name
      };
      
      // 启动工作流
      const instance = await this.workflowEngine.start(trigger.workflowId, {
        variables
      });
      
      logger.info(`Workflow triggered: ${trigger.workflowId} by ${trigger.name}`);
      this.emit('workflowTriggered', {
        trigger,
        context,
        instance
      });
      
    } catch (error: any) {
      logger.error(`Failed to trigger workflow for ${trigger.id}:`, error);
      this.emit('triggerError', {
        trigger,
        context,
        error
      });
    }
  }
  
  /**
   * 设置定时器
   */
  private async setupScheduler(trigger: WorkflowTrigger): Promise<void> {
    // TODO: 实现基于 cron 的定时触发器
    logger.warn(`Schedule triggers not yet implemented for ${trigger.id}`);
  }
  
  /**
   * 验证触发器配置
   */
  private validateTrigger(trigger: WorkflowTrigger): void {
    if (!trigger.id || !trigger.name) {
      throw new Error('Trigger must have id and name');
    }
    
    if (!trigger.workflowId) {
      throw new Error('Trigger must specify a workflow to trigger');
    }
    
    if (!trigger.conditions || trigger.conditions.length === 0) {
      throw new Error('Trigger must have at least one condition');
    }
  }
  
  /**
   * 启用触发器
   */
  async enableTrigger(triggerId: string): Promise<void> {
    const trigger = this.triggers.get(triggerId);
    if (!trigger) {
      throw new Error(`Trigger not found: ${triggerId}`);
    }
    
    if (!trigger.enabled) {
      trigger.enabled = true;
      await this.setupTriggerListener(trigger);
      logger.info(`Enabled trigger: ${trigger.name}`);
    }
  }
  
  /**
   * 禁用触发器
   */
  async disableTrigger(triggerId: string): Promise<void> {
    const trigger = this.triggers.get(triggerId);
    if (!trigger) {
      throw new Error(`Trigger not found: ${triggerId}`);
    }
    
    if (trigger.enabled) {
      trigger.enabled = false;
      
      // 停止文件监听
      const watcher = this.watchers.get(triggerId);
      if (watcher) {
        await watcher.close();
        this.watchers.delete(triggerId);
      }
      
      // 清除防抖定时器
      const timer = this.debounceTimers.get(triggerId);
      if (timer) {
        clearTimeout(timer);
        this.debounceTimers.delete(triggerId);
      }
      
      logger.info(`Disabled trigger: ${trigger.name}`);
    }
  }
  
  /**
   * 获取所有触发器
   */
  getTriggers(): WorkflowTrigger[] {
    return Array.from(this.triggers.values());
  }
  
  /**
   * 获取触发器状态
   */
  getTriggerStatus(triggerId: string): any {
    const trigger = this.triggers.get(triggerId);
    if (!trigger) {
      return null;
    }
    
    const lastTrigger = this.lastTriggerTime.get(triggerId);
    const hasDebounceTimer = this.debounceTimers.has(triggerId);
    const hasWatcher = this.watchers.has(triggerId);
    
    return {
      ...trigger,
      lastTriggered: lastTrigger ? new Date(lastTrigger).toISOString() : null,
      isDebouncing: hasDebounceTimer,
      isWatching: hasWatcher
    };
  }
  
  /**
   * 停止所有触发器
   */
  async stop(): Promise<void> {
    // 停止所有文件监听
    for (const [id, watcher] of this.watchers) {
      await watcher.close();
    }
    this.watchers.clear();
    
    // 清除所有定时器
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
    
    logger.info('Workflow trigger manager stopped');
  }
}